import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

const gateway = createHttpClient(serviceUrls.gateway);
const reportsBase = `${serviceUrls.gateway.replace(/\/$/, '')}/v1/reports`;

export type WeeklyDigestResponse = {
  report_version: string;
  student: { id: string; display_name?: string; grade?: number };
  period: { start_date: string; end_date: string; timezone?: string };
  lesson_report: {
    totals?: { sessions?: number; minutes?: number; achieved?: number; needs_practice?: number };
    can_dos?: Array<{ can_do_id: string; statement_en?: string; mastery?: string; score?: number }>;
  };
  communication_report: {
    totals?: { sessions?: number; minutes?: number; achieved?: number; needs_practice?: number };
    can_dos?: Array<{ can_do_id: string; statement_en?: string; mastery?: string }>;
    sessions_approx?: number;
  };
  engagement?: {
    active_days: number;
    longest_streak_days: number;
    engagement_score: number;
    engagement_label: 'LOW' | 'MEDIUM' | 'HIGH';
    avg_session_duration_minutes: number;
    total_learning_minutes: number;
  };
  growth_trends?: {
    lesson_mastery_delta: number;
    communication_mastery_delta: number;
    sessions_delta: number;
    minutes_delta: number;
    trend_label: 'IMPROVING' | 'DECLINING' | 'STABLE' | 'INSUFFICIENT_DATA';
  };
  skill_breakdown?: {
    speaking?: { mastery?: number; sessions?: number; minutes?: number; trend?: string };
    listening?: { mastery?: number; sessions?: number; minutes?: number; trend?: string };
    reading?: { mastery?: number; sessions?: number; minutes?: number; trend?: string };
    writing?: { mastery?: number; sessions?: number; minutes?: number; trend?: string };
  };
  assignments?: { completed: number; pending: number; overdue: number; completion_rate: number };
  performance_metrics?: {
    first_attempt_success_rate: number;
    average_attempts_per_can_do: number;
    retry_rate: number;
  };
  highlights?: {
    strongest_skill?: string;
    needs_focus_skill?: string;
    best_can_do?: string;
    most_improved_area?: string;
  };
  recommendations?: Array<{ type: 'FOCUS' | 'PRACTICE' | 'CELEBRATION'; message: string; confidence?: number }>;
  alerts?: Array<{ type: string; message: string; severity?: 'INFO' | 'WARNING' }>;
  benchmarks?: { grade_percentile?: number | null };
  grade_resolution_failed?: boolean;
  grade_resolution_source?: string | null;
  diagnostics?: {
    partial_failures?: string[];
    warnings?: string[];
    generation_metrics?: { digest_ms?: number; assignments_ms?: number; render_ms?: number };
  };
};

/** Query params for GET /v1/tutor/reports/weekly-student-digest (FastAPI). */
export function buildWeeklyStudentDigestQuery(params: {
  studentId: string;
  /** Roster student id from teacher dropdown — must match communication-logs key in Mongo. */
  rosterStudentId?: string;
  startDate?: string;
  endDate?: string;
  grade?: number;
}): Record<string, string | number> {
  const studentId = params.studentId.trim();
  if (!studentId) {
    throw new Error('studentId is required for weekly student digest');
  }
  const query: Record<string, string | number> = { student_id: studentId };
  if (params.startDate) query.start_date = params.startDate;
  if (params.endDate) query.end_date = params.endDate;
  const roster = params.rosterStudentId?.trim();
  if (roster) query.roster_student_id = roster;
  if (params.grade != null && Number.isFinite(params.grade)) query.grade = params.grade;
  return query;
}

export async function getWeeklyDigest(params: {
  studentId: string;
  /** Roster student id from teacher dropdown — must match communication-logs key in Mongo. */
  rosterStudentId?: string;
  startDate?: string;
  endDate?: string;
  grade?: number;
}): Promise<WeeklyDigestResponse> {
  const r = await gateway.get<WeeklyDigestResponse>('/v1/tutor/reports/weekly-student-digest', {
    params: buildWeeklyStudentDigestQuery(params),
  });
  return r.data;
}

export type WeeklyCohortDigestResponse = {
  report_version: string;
  scope: {
    class_id?: string | null;
    class_name?: string | null;
    grade?: number | null;
    section_id?: string | null;
  };
  period: { start_date: string; end_date: string; timezone?: string | null };
  summary: {
    total_students: number;
    active_students: number;
    inactive_students: number;
    no_data_students: number;
    digest_failed_students: number;
    evaluable_students: number;
  };
  aggregates: {
    average_engagement_score?: number | null;
    average_active_days?: number | null;
    average_learning_minutes?: number | null;
    average_sessions_delta?: number | null;
    average_minutes_delta?: number | null;
    total_completed_assignments: number;
    total_pending_assignments: number;
    total_overdue_assignments: number;
    improving_count: number;
    declining_count: number;
    stable_count: number;
    insufficient_growth_data_count: number;
  };
  risk_summary: { high: number; medium: number; low: number };
  student_rows: Array<{
    student_id: string;
    roster_student_id?: string | null;
    student_name: string;
    status: 'active' | 'inactive' | 'no_data' | 'digest_failed';
    risk_level?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
    engagement?: WeeklyDigestResponse['engagement'];
    growth?: WeeklyDigestResponse['growth_trends'];
    assignments?: WeeklyDigestResponse['assignments'];
    active_days: number;
    reason?: string | null;
  }>;
};

export function buildWeeklyCohortDigestQuery(params: {
  classId?: string;
  grade?: string | number;
  sectionId?: string;
  startDate?: string;
  endDate?: string;
}): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  const classId = params.classId?.trim();
  if (classId) query.class_id = classId;
  const grade = params.grade;
  if (grade != null && String(grade).trim()) query.grade = String(grade).trim();
  const sectionId = params.sectionId?.trim();
  if (sectionId) query.section_id = sectionId;
  if (params.startDate) query.start_date = params.startDate;
  if (params.endDate) query.end_date = params.endDate;
  return query;
}

export async function getWeeklyCohortDigest(params: {
  classId?: string;
  grade?: string | number;
  sectionId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<WeeklyCohortDigestResponse> {
  const r = await gateway.get<WeeklyCohortDigestResponse>(
    '/v1/tutor/reports/weekly-cohort-digest',
    { params: buildWeeklyCohortDigestQuery(params) },
  );
  return r.data;
}

export type WeeklyReportSendResult = {
  email?: string;
  type?: string;
  status?: string;
  error?: string;
};

export async function sendWeeklyReportNow(body: {
  tenant_id: string;
  student_id: string;
  roster_student_id?: string;
  start_date?: string;
  end_date?: string;
  force_resend?: boolean;
}): Promise<{
  student_id?: string;
  results?: WeeklyReportSendResult[];
  skipped?: boolean;
  reason?: string;
  [key: string]: unknown;
}> {
  const r = await gateway.post<{
    student_id?: string;
    results?: WeeklyReportSendResult[];
    skipped?: boolean;
    reason?: string;
    [key: string]: unknown;
  }>('/v1/reports/jobs/send-now', body);
  return r.data;
}

export async function sendSchoolAdminReportNow(body: {
  tenant_id: string;
  start_date?: string;
  end_date?: string;
  week_key?: string;
  force_resend?: boolean;
}): Promise<Record<string, unknown>> {
  const r = await gateway.post<Record<string, unknown>>(
    '/v1/reports/jobs/send-school-admin-now',
    body
  );
  return r.data;
}

export async function getDeliveryLogs(params: {
  tenant_id: string;
  student_id: string;
  week_key?: string;
}): Promise<{ items: unknown[] }> {
  const r = await gateway.get<{ items: unknown[] }>('/v1/reports/jobs/delivery-logs', { params });
  return r.data;
}
